import { Injectable } from '@angular/core';
import { ProductType } from '@core/models/api/dsm-types';
import { ErrorSanitizerService } from '@core/services/error-sanitizer.service';
import { LoadingService } from '@core/services/loading.service';
import { ProductsService } from '@core/services/products.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, forkJoin, timer, Observable } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
  retry,
} from 'rxjs/operators';
import {
  transferQuote,
  transferQuoteSuccess,
  transferQuoteError,
  transferMultipleQuotes,
  transferMultipleQuotesSuccess,
} from './transfer.action';
import { QuoteTransferAdapter } from '@core/adapters/quote-transfer.adapter';
import { retrieveQuotes } from '@core/store/retrieve/retrieve.action';
import { QuoteAdapter } from '@core/adapters/quote.adapter';

// TODO come up with a better strategy for retrieve delays. Polling somehow maybe?
const RETRIEVE_DELAY = 1500;

@Injectable()
export class TransferEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly transferAdapter: QuoteTransferAdapter,
    private readonly loadingService: LoadingService,
    private readonly errorSanitizerService: ErrorSanitizerService,
    private readonly productsService: ProductsService,
    private readonly quoteAdapter: QuoteAdapter,
  ) {}

  // #region transferQuote$
  transferQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transferQuote),
      tap(() =>
        this.loadingService.beginLoading(
          'transfer-quote',
          'Transferring quote...'
        )
      ),
      switchMap(({ payload }) =>
        this.transferAdapter
          .transfer(
            payload.quoteId.split('-')[0],
            payload.producerCode,
            payload.productType
          )
          .pipe(
            tap(() => this.loadingService.endLoading('transfer-quote')),
            map((response) =>
              transferQuoteSuccess({
                payload: {
                  quoteId: response.quoteId,
                  producerCode: payload.producerCode,
                  productType: payload.productType,
                },
              })
            ),
            catchError((error) => {
              this.loadingService.endLoading('transfer-quote');
              return of(
                transferQuoteError({
                  payload: this.errorSanitizerService.sanitizeError(error),
                })
              );
            })
          )
      )
    )
  );
  // #endregion

  // #region transferQuoteSuccess$
  transferQuoteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transferQuoteSuccess),
      tap(() =>
        this.loadingService.beginLoading(
          'transfer-to-retrieve',
          'Quote transferred, begin retrieve...'
        )
      ),
      switchMap(({ payload }) => {
        const productType: ProductType = payload?.productType
          ? payload?.productType
          : 'Homeowner';
        const quoteNumber = payload.quoteId.split('-')[0];

        // delete unselected versions
        return this.quoteAdapter.getQuoteVersions(productType, quoteNumber).pipe(
          switchMap((versions) => {
            const unselectedVersions = versions.filter(v => v.isSelected === false);
            const deleteQuoteVersions = unselectedVersions.map(v =>
              this.quoteAdapter.deleteQuoteVersion(productType, v.quoteNumber, v.versionNumber.toString())
            );
            return (deleteQuoteVersions.length > 0 ? forkJoin(deleteQuoteVersions) : of(null));
          }),
          catchError(() => of(null)),
          switchMap(() =>
            timer(RETRIEVE_DELAY).pipe(
              tap(() => this.loadingService.endLoading('transfer-to-retrieve')),
              map(() =>
                retrieveQuotes({
                  payload: {
                    quotes: [
                      {
                        quoteId: quoteNumber,
                        productType: productType,
                      },
                    ],
                  },
                })
              )
            )
          )
        );
      })
    )
  );
  // #endregion

  // #region transferMultipleQuotes$
  transferMultipleQuotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transferMultipleQuotes),
      tap(() =>
        this.loadingService.beginLoading(
          'transfer-multiple-quotes',
          'Transferring quotes...'
        )
      ),
      switchMap(({ payload }) => {
        const { quotes, producerCode } = payload;

        if (quotes.length === 0) {
          return this.completeTransferWithQuotes([], producerCode);
        }

        return this.transferFirstQuote(quotes[0], producerCode).pipe(
          switchMap((firstTransferResult) =>
            this.transferRemainingQuotes(
              quotes,
              firstTransferResult,
              producerCode
            )
          ),
          catchError((error) => {
            this.loadingService.endLoading('transfer-multiple-quotes');
            return of(
              transferQuoteError({
                payload: this.errorSanitizerService.sanitizeError(error),
              })
            );
          })
        );
      })
    )
  );
  // #endregion

  // #region transferMultipleQuotesSuccess$
  transferMultipleQuotesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transferMultipleQuotesSuccess),
      tap(() =>
        this.loadingService.beginLoading(
          'transfer-multiple-to-retrieve',
          'Quotes transferred, begin retrieve...'
        )
      ),
      switchMap(({ payload }) => {
        const quotesToRetrieve = payload.quotes.map((quote) => ({
          quoteId: quote.quoteId.split('-')[0],
          productType: quote.productType,
        }));

        // delete unselected versions
        const deleteQuoteVersions = quotesToRetrieve.map(({ quoteId, productType }) =>
          this.quoteAdapter.getQuoteVersions(productType, quoteId).pipe(
            switchMap((versions) => {
              const unselectedVersions = versions.filter(v => v.isSelected === false);
              const deleteCalls = unselectedVersions.map(v =>
                this.quoteAdapter.deleteQuoteVersion(productType, v.quoteNumber, v.versionNumber.toString())
              );
              return deleteCalls.length > 0 ? forkJoin(deleteCalls) : of(null);
            }),
            catchError(() => of(null))
          )
        );

        return forkJoin(deleteQuoteVersions).pipe(
          catchError(() => of(null)),
          switchMap(() =>
            timer(RETRIEVE_DELAY).pipe(
              tap(() =>
                this.loadingService.endLoading('transfer-multiple-to-retrieve')
              ),
              map(() =>
                retrieveQuotes({
                  payload: { quotes: quotesToRetrieve },
                })
              )
            )
          )
        );
      })
    )
  );
  // #endregion

  // #region transferQuoteError$
  transferQuoteError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(transferQuoteError),
        tap(() => {
          this.loadingService.endLoading('transfer-quote');
        })
      ),
    { dispatch: false }
  );
  // #endregion

  // #region transferFirstQuote
  private transferFirstQuote(
    quote: { quoteId: string; productType: ProductType },
    producerCode: string
  ): Observable<{
    quoteId: string;
    productType: ProductType;
    accountNumber: string;
  }> {
    return this.transferAdapter
      .transfer(
        quote.quoteId.split('-')[0],
        producerCode,
        quote.productType
      )
      .pipe(
        map((response) => ({
          quoteId: response.quoteId,
          productType: quote.productType,
          accountNumber: response.accountNumber,
        })),
        this.retryHelper<{
          quoteId: string;
          productType: ProductType;
          accountNumber: string;
        }>('First quote transfer', quote.quoteId),
        catchError((error) => {
          throw error;
        })
      );
  }
  // #endregion

  // #region transferRemainingQuotes
  private transferRemainingQuotes(
    allQuotes: { quoteId: string; productType: ProductType }[],
    firstTransferResult: {
      quoteId: string;
      productType: ProductType;
      accountNumber: string;
    },
    producerCode: string
  ) {
    if (allQuotes.length <= 1) {
      return this.completeTransferWithQuotes(
        [firstTransferResult],
        producerCode
      );
    }

    const remainingQuotes = allQuotes.slice(1);
    const transferObservables = remainingQuotes.map((quote) =>
      this.transferQuoteToExistingAccount(
        quote,
        producerCode,
        firstTransferResult.accountNumber
      )
    );

    return forkJoin(transferObservables).pipe(
      map((results) => {
        const successfulTransfers = results.filter(
          (
            result
          ): result is {
            quoteId: string;
            productType: ProductType;
            accountNumber: string;
          } => result !== null
        );
        const allTransferredQuotes = [
          firstTransferResult,
          ...successfulTransfers,
        ];
        this.loadingService.endLoading('transfer-multiple-quotes');
        return transferMultipleQuotesSuccess({
          payload: { quotes: allTransferredQuotes, producerCode },
        });
      })
    );
  }
  // #endregion

  // #region transferToExistingAccount
  private transferQuoteToExistingAccount(
    quote: { quoteId: string; productType: ProductType },
    producerCode: string,
    accountNumber: string
  ) {
    return this.transferAdapter
      .transferToExistingAccount(
        quote.quoteId.split('-')[0],
        producerCode,
        quote.productType,
        accountNumber
      )
      .pipe(
        map((response) => ({
          quoteId: quote.quoteId, // Preserve original quote ID
          productType: quote.productType,
          accountNumber: response.accountNumber,
        })),
        this.retryHelper<{
          quoteId: string;
          productType: ProductType;
          accountNumber: string;
        }>('Quote transfer to existing account', quote.quoteId),
        catchError(() => {
          return of(null);
        })
      );
  }
  // #endregion

  // #region completeTransferWithQuotes
  private completeTransferWithQuotes(
    quotes: {
      quoteId: string;
      productType: ProductType;
      accountNumber: string;
    }[],
    producerCode: string
  ) {
    this.loadingService.endLoading('transfer-multiple-quotes');
    return of(
      transferMultipleQuotesSuccess({
        payload: { quotes, producerCode },
      })
    );
  }
  // #endregion

  // #region retryHelper
  private retryHelper<T>(
    operation: string,
    quoteId: string,
    maxRetries: number = 3
  ): any {
    return retry({
      count: maxRetries,
      delay: (retryCount) => {
        const delay = Math.pow(2, retryCount - 1) * 1000; // Exponential backoff: 1s, 2s, 4s
        return timer(delay);
      },
    });
  }
  // #endregion
}
